"use client";

import React, { useState, useEffect } from "react";
import { Wallet, Shield, Clock, CheckCircle, Copy } from "lucide-react";
import { SiApple, SiGooglepay, SiCashapp } from "react-icons/si";
import { usePrivy, useFundWallet } from "@privy-io/react-auth";
import  * as ethers  from "ethers";
import { base } from "viem/chains";
import toast, { Toaster } from "react-hot-toast";
import { getBalance } from "../services/authapi";

interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  fee: string;
}

export function AddFundsPage() {
  const { user } = usePrivy();
  const { fundWallet } = useFundWallet();
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [userWallet, setUserWallet] = useState<string>("");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [isOnrampModalOpen, setIsOnrampModalOpen] = useState<boolean>(false);

  console.log("Privy user:", user);
  console.log("User wallet:", userWallet);
  console.log("Privy env appId:", import.meta.env.VITE_PRIVY_APPID);
  console.log("Privy env clientId:", import.meta.env.VITE_PRIVY_CLIENTID);

  // Define payment methods, including new Stripe OnRamp option
  const paymentMethods: PaymentMethod[] = [
    {
      id: "applepay",
      name: "Apple Pay",
      icon: <SiApple className="w-6 h-6" />,
      fee: "0",
    },
    {
      id: "googlepay",
      name: "Google Pay",
      icon: <SiGooglepay className="w-6 h-6" />,
      fee: "0",
    },
    {
      id: "card",
      name: "Credit/Debit Card",
      icon: <span className="text-lg">💳</span>,
      fee: "0",
    },
    {
      id: "wallet",
      name: "Transfer From Wallet",
      icon: <span className="text-lg">💳</span>,
      fee: "0",
    },
    {
      id: "cashapp",
      name: "Cash App",
      icon: <SiCashapp className="w-6 h-6" />,
      fee: "0",
    },
    {
      id: "stripe",
      name: "Stripe OnRamp",
      icon: <span className="text-lg">💳</span>,
      fee: "0",
    },
  ];

  // Fetch user's wallet address
  useEffect(() => {
    if (user) {
      const wallets = user.linkedAccounts
        .filter((account) => account.type === "smart_wallet")
        .map((wallet) => wallet.address);
      if (wallets.length > 0 && ethers.isAddress(wallets[0])) {
        setUserWallet(wallets[0]);
      }
    }
  }, [user]);

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!userWallet || !ethers.isAddress(userWallet)) {
        return;
      }

      setLoading(true);
      try {
        const ethBalance = await getBalance(userWallet);
        setWalletBalance(ethBalance);
      } catch (err: any) {
        console.error("Error fetching USDC balance:", err.message);
        toast.error("Failed to fetch wallet balance. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [userWallet]);

  // Load Stripe scripts and mount OnRamp session when modal is open
  useEffect(() => {
    if (clientSecret && isOnrampModalOpen) {
      const stripeScript = document.createElement("script");
      stripeScript.src = "https://js.stripe.com/v3/";
      stripeScript.async = true;
      document.body.appendChild(stripeScript);

      stripeScript.onload = () => {
        const onrampScript = document.createElement("script");
        onrampScript.src =
          "https://crypto-js.stripe.com/crypto-onramp-outer.js";
        onrampScript.async = true;
        document.body.appendChild(onrampScript);

        onrampScript.onload = () => {
          // @ts-ignore
          const onramp = new window.StripeOnramp(
            import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY
          );
          const session = onramp.createSession({ clientSecret });
          session.mount("#onramp-container");
        };

        onrampScript.onerror = () => {
          console.error("Failed to load Stripe Onramp script");
          toast.error("Failed to load Stripe Onramp. Please try again.");
        };
      };

      stripeScript.onerror = () => {
        console.error("Failed to load StripeJS script");
        toast.error("Failed to load Stripe library. Please try again.");
      };

      return () => {
        if (document.body.contains(stripeScript)) {
          document.body.removeChild(stripeScript);
        }
        const onrampScript = document.querySelector(
          'script[src="https://crypto-js.stripe.com/crypto-onramp-outer.js"]'
        );
        if (onrampScript && document.body.contains(onrampScript)) {
          document.body.removeChild(onrampScript);
        }
      };
    }
  }, [clientSecret, isOnrampModalOpen]);
  console.log(base);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!userWallet || !amount) {
        throw new Error("Please select a wallet and enter an amount.");
      }

      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount < 5 || parsedAmount > 10000) {
        throw new Error("Amount must be between $5 and $10,000.");
      }

      if (selectedMethod === "cashapp") {
        toast.error(
          "Cash App is not supported for direct on-ramping. Please select another payment method."
        );
        setLoading(false);
        return;
      }

      if (selectedMethod === "stripe") {
        const response = await fetch(
          `${import.meta.env.VITE_SERVERURL}/create-onramp-session`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              transaction_details: {
                destination_currency: "usdc",
                destination_exchange_amount: parsedAmount.toString(),
                destination_amount: parsedAmount.toString(),
                destination_network: "base",
                destination_address: userWallet,
                wallet_address: userWallet,
              },
            }),
          }
        );

        const data = await response.json();
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
          setIsOnrampModalOpen(true); // Open the OnRamp modal
          toast.success("Stripe OnRamp session initiated!");
        } else {
          throw new Error("Failed to get client secret from session.");
        }

        setLoading(false);
        return;
      }
      console.log(userWallet);

      if (!ethers.isAddress(userWallet)) {
        console.log("Wallet is not a valid address");
        return;
      }
      await fundWallet({
        address: userWallet,
        options: {
          chain: base,
          amount: parsedAmount.toString(),
          asset: "USDC",
        },
      });

      toast.success("Funding initiated successfully!");
      setLoading(false);
    } catch (err: any) {
      toast.error("Failed to initiate on-ramp. Please try again.");
      console.error("Error initiating funding:", err);
      setLoading(false);
    }
  };

  // Calculate processing fee
  const calculateFee = (): number => {
    const method = paymentMethods.find((m) => m.id === selectedMethod);
    if (!method || !amount) return 0;
    return (parseFloat(amount) * parseFloat(method.fee.replace("%", ""))) / 100;
  };

  // Calculate total amount including fee
  const calculateTotal = (): number => {
    return parseFloat(amount || "0") + calculateFee();
  };

  // Copy wallet address to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Address copied to clipboard!");
  };

  // Handle OnRamp modal close
  const handleOnrampModalClose = () => {
    setIsOnrampModalOpen(false);
    setClientSecret(""); // Reset clientSecret to allow re-initiation
  };

  // OnRamp modal content
  const OnrampModalContent = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[1000] p-2 md:p-6">
      <div className="bg-white rounded-lg w-full max-w-[90vw] md:max-w-[600px] relative p-4 md:p-8 max-h-[90vh] overflow-auto">
        <div
          id="onramp-container"
          style={{ height: "80vh", minHeight: "400px", width: "100%" }}
        ></div>
        <div className="flex justify-end mt-4">
          <button
            onClick={handleOnrampModalClose}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen max-w-5xl mx-auto bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white p-4 md:p-8">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      {isOnrampModalOpen && <OnrampModalContent />}
      <div className="w-full space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            Add Funds
          </h1>
          <p className="text-gray-400 text-base md:text-lg">
            Top up your wallet with USDC on Base using your preferred payment
            method
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Wallet Display */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4">
                  Your Wallet
                </h3>
                <div className="p-4 bg-[#61CE70]/10 border border-[#61CE70]/30 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#61CE70]/20 rounded-lg">
                        <Wallet className="w-5 h-5 text-[#61CE70]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">PayzTap Wallet</p>
                        <p className="text-gray-400 text-sm font-mono">
                          {truncateAddress(userWallet)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(userWallet)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      type="button"
                    >
                      <Copy className="w-4 h-4 text-gray-400 hover:text-[#61CE70]" />
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#61CE70]/20">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">
                        Current Balance:
                      </span>
                      <span className="text-[#61CE70] font-medium">
                        {loading
                          ? "Loading..."
                          : `${parseFloat(walletBalance)} USDC`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4">
                  Enter Amount
                </h3>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                    $
                  </span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white text-lg placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    placeholder="0.00"
                    min="5"
                    max="10000"
                    step="0.01"
                    required
                  />
                </div>
                <p className="text-gray-400 text-sm mt-2">
                  Minimum: $5 | Maximum: $10,000
                </p>
              </div>

              {/* Payment Methods */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4">
                  Select Payment Method
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setSelectedMethod(method.id)}
                      className={`p-4 md:p-6 rounded-2xl border transition-all duration-200 ${
                        selectedMethod === method.id
                          ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                          : "border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
                              method.id === "cashapp"
                                ? "bg-green-600"
                                : method.id === "applepay"
                                ? "bg-black"
                                : method.id === "googlepay"
                                ? "bg-blue-500"
                                : method.id === "stripe"
                                ? "bg-purple-600"
                                : "bg-gray-600"
                            }`}
                          >
                            {method.icon}
                          </div>
                          <span
                            className={`font-medium ${
                              selectedMethod === method.id
                                ? "text-[#61CE70]"
                                : "text-white"
                            }`}
                          >
                            {method.name}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Manage Payment Methods */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4">
                  Manage Payment Methods
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    className="p-4 md:p-6 rounded-2xl border border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white bg-blue-600">
                        <span className="text-lg">🏦</span>
                      </div>
                      <span className="font-medium text-white">
                        Link Bank Account
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="p-4 md:p-6 rounded-2xl border border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white bg-gray-600">
                        <span className="text-lg">💳</span>
                      </div>
                      <span className="text-medium text-white">Add Card</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-sm order-last">
                <h3 className="text-lg md:text-xl font-bold text-white mb-4">
                  Order Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount</span>
                    <span className="text-white">${amount || "0.00"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Processing Fee</span>
                    <span className="text-white">${calculateFee()}</span>
                  </div>
                  <div className="border-t border-gray-700/50 pt-3">
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Total</span>
                      <span className="text-[#61CE70] font-bold text-lg">
                        ${calculateTotal()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-700/50">
                  <h4 className="text-base md:text-lg font-bold text-white mb-4">
                    Security Features
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-[#61CE70]" />
                      <span className="text-gray-300">
                        256-bit SSL encryption
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-[#61CE70]" />
                      <span className="text-gray-300">Instant processing</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#61CE70]" />
                      <span className="text-gray-300">KYC/AML compliant</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!selectedMethod || !amount || !userWallet || loading}
                className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-4 rounded-2xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Continue to Payment"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function truncateAddress(address: string): string {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export default AddFundsPage;
